import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Megaphone, Calendar, Bot, Zap, ChevronRight, Clock, Users,
  Briefcase, MessageSquare, BookOpen, Monitor, Database, Shield,
  LifeBuoy, Package, Plane, Lightbulb, GraduationCap, Gift, Bell,
  Sparkles, ArrowRight, Loader2, MapPin, CheckSquare, Target,
  Star, Globe, Cpu,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_COLORS = {
  Jobs: "bg-blue-100 text-blue-700",
  Bursary: "bg-emerald-100 text-emerald-700",
  Alert: "bg-red-100 text-red-700",
  Incident: "bg-amber-100 text-amber-700",
  Events: "bg-purple-100 text-purple-700",
  "Lost & Found": "bg-orange-100 text-orange-700",
  Acting: "bg-cyan-100 text-cyan-700",
  Death: "bg-slate-100 text-slate-700",
  General: "bg-violet-100 text-violet-700",
};

// ── SA-Context Fallback Data ──
const FALLBACK_BROADCASTS = [
  {
    id: "bc_001", category: "Jobs", title: "Vacancy: Deputy Director: ICT Governance (DPSA)",
    body: "The Department of Public Service and Administration invites applications for DD: ICT Governance. Salary: R1,200,000 - R1,400,000 per annum. Location: Pretoria. Applications close 15 June 2026.",
    created_at: "2026-05-10T08:00:00Z", expires_at: "2026-06-15T23:59:00Z", closing_date: "2026-06-15",
  },
  {
    id: "bc_002", category: "Bursary", title: "SAPSA Bursary Programme 2026",
    body: "The South African Police Service (SAPS) is offering full-time bursaries for LLB, Forensic Accounting, and IT degrees. Applicants must be SA citizens with a valid Grade 12 certificate.",
    created_at: "2026-05-08T10:30:00Z", expires_at: "2026-07-31T23:59:00Z", closing_date: "2026-07-31",
  },
  {
    id: "bc_003", category: "Alert", title: "Load Shedding Schedule Update — Week of 18 May",
    body: "Eskom has published the load shedding schedule for the week of 18 May 2026. Stage 2 load shedding will be implemented from 16:00 to 22:00 daily. Please plan accordingly and ensure backup power systems are tested.",
    created_at: "2026-05-17T14:00:00Z", expires_at: "2026-05-25T23:59:00Z",
  },
  {
    id: "bc_004", category: "General", title: "Office Relocation: DIRCO Pretoria Head Office",
    body: "The Department of International Relations and Cooperation (DIRCO) will be relocating from the OR Tambo Building to the new Centurion Campus effective 1 July 2026. All staff are required to complete the asset movement form by 15 June.",
    created_at: "2026-05-05T09:00:00Z", expires_at: "2026-07-01T00:00:00Z",
  },
  {
    id: "bc_005", category: "Events", title: "Public Service Commission Annual Symposium 2026",
    body: "The PSC Annual Symposium will be held at the CSIR International Convention Centre, Pretoria on 12-14 June 2026. Theme: 'Ethics and Innovation in the Public Service'. Registration is open to all government employees.",
    created_at: "2026-05-01T07:00:00Z", expires_at: "2026-06-14T23:59:00Z", closing_date: "2026-06-05",
  },
  {
    id: "bc_006", category: "Incident", title: "Power Outage — Union Buildings Precinct",
    body: "Due to a transformer fault, the Union Buildings and surrounding government offices will experience a power outage on 20 May 2026 from 09:00 to 14:00. Essential services will be on generator backup.",
    created_at: "2026-05-18T06:00:00Z", expires_at: "2026-05-21T23:59:00Z",
  },
  {
    id: "bc_007", category: "Acting", title: "Acting Appointment: CFO — National Treasury",
    body: "Mr. Thabo Mokoena has been appointed as Acting Chief Financial Officer of the National Treasury effective 1 June 2026, pending the finalisation of the recruitment process.",
    created_at: "2026-05-15T11:00:00Z", expires_at: "2026-06-30T23:59:00Z",
  },
  {
    id: "bc_008", category: "Jobs", title: "Senior Investigator: Public Protector South Africa",
    body: "The Public Protector is seeking a Senior Investigator with 10+ years experience in forensic investigations. Based in Pretoria with travel to provincial offices. Salary: R1,050,000 per annum. Closing date: 30 June 2026.",
    created_at: "2026-05-12T08:30:00Z", expires_at: "2026-06-30T23:59:00Z", closing_date: "2026-06-30",
  },
];

const FALLBACK_EVENTS = [
  { id: "ev_001", date: "2026-05-25", title: "Closing: DPSA DD ICT Governance", type: "closing" },
  { id: "ev_002", date: "2026-06-05", title: "Closing: PSC Symposium Registration", type: "closing" },
  { id: "ev_003", date: "2026-06-12", title: "PSC Annual Symposium Day 1", type: "event" },
  { id: "ev_004", date: "2026-06-14", title: "PSC Annual Symposium Day 2", type: "event" },
  { id: "ev_005", date: "2026-06-15", title: "Closing: DIRCO Asset Movement Form", type: "closing" },
  { id: "ev_006", date: "2026-06-30", title: "Closing: Public Protector Investigator", type: "closing" },
  { id: "ev_007", date: "2026-07-01", title: "DIRCO Centurion Campus Opens", type: "event" },
  { id: "ev_008", date: "2026-08-09", title: "Women's Day Public Holiday", type: "event" },
  { id: "ev_009", date: "2026-08-31", title: "Closing: SAPSA Bursary Programme", type: "closing" },
  { id: "ev_010", date: "2026-09-24", title: "Heritage Day Celebrations", type: "event" },
];

const HUBS = [
  { path: "/smart-office", icon: Briefcase, name: "Smart Office", color: "from-rose-500 to-pink-600", desc: "Docs, Excel, Translate" },
  { path: "/vetting", icon: Shield, name: "Smart Vetting", color: "from-indigo-500 to-indigo-600", desc: "Vetting & classification" },
  { path: "/ims", icon: Database, name: "IMS Hub", color: "from-emerald-500 to-emerald-600", desc: "Intelligence correlation" },
  { path: "/comms-centre", icon: MessageSquare, name: "Comms Centre", color: "from-violet-500 to-purple-600", desc: "Chat, calls, meetings" },
  { path: "/hr-hub", icon: Users, name: "Human Centre", color: "from-emerald-500 to-teal-600", desc: "HR, organogram, performance" },
  { path: "/monitoring-centre", icon: Monitor, name: "Monitoring", color: "from-red-500 to-red-600", desc: "Live feeds & alerts" },
  { path: "/learning", icon: GraduationCap, name: "Learning", color: "from-amber-500 to-yellow-600", desc: "Courses & resources" },
  { path: "/innovation-centre", icon: Lightbulb, name: "Innovation", color: "from-purple-500 to-purple-600", desc: "Idea management" },
  { path: "/knowledge-centre", icon: BookOpen, name: "Knowledge Hub", color: "from-cyan-500 to-cyan-600", desc: "SOPs & documentation" },
  { path: "/alerts", icon: Bell, name: "Alerts Hub", color: "from-blue-500 to-blue-600", desc: "News & weather alerts" },
  { path: "/rewards", icon: Gift, name: "Rewards Centre", color: "from-amber-500 to-orange-600", desc: "Competitions & credits" },
  { path: "/logistics", icon: Package, name: "Logistics Hub", color: "from-amber-500 to-orange-600", desc: "Assets, fleet & SCM" },
  { path: "/travelling", icon: Plane, name: "Travelling Admin", color: "from-sky-500 to-blue-600", desc: "Trip & expense management" },
  { path: "/request-service", icon: LifeBuoy, name: "Request Service", color: "from-teal-500 to-emerald-600", desc: "IT, medical & office requests" },
];

export default function NoticeBoard() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [broadcasts, setBroadcasts] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  useEffect(() => {
    const fetch = async () => {
      try {
        const token = localStorage.getItem("token");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};

        // Try to fetch broadcasts, gracefully fall back if not available
        try {
          const [bcRes, evRes] = await Promise.all([
            axios.get(`${API}/broadcast?active_only=true`, { headers }).catch(() => ({ data: [] })),
            axios.get(`${API}/broadcast/calendar/events`, { headers }).catch(() => ({ data: [] })),
          ]);
          setBroadcasts(bcRes.data?.length > 0 ? bcRes.data : FALLBACK_BROADCASTS);
          setEvents(evRes.data?.length > 0 ? evRes.data : FALLBACK_EVENTS);
        } catch {
          // Broadcast router might not be seeded yet
        }
      } catch (err) {
        console.error("NoticeBoard fetch error:", err);
      }
      setLoading(false);
    };
    fetch();
  }, []);

  // Calendar helpers
  const today = new Date();
  const daysInMonth = new Date(selectedYear, selectedMonth + 1, 0).getDate();
  const firstDayOfWeek = new Date(selectedYear, selectedMonth, 1).getDay();

  const monthEvents = events.filter((e) => {
    const d = new Date(e.date);
    return d.getMonth() === selectedMonth && d.getFullYear() === selectedYear;
  });

  const prevMonth = () => {
    if (selectedMonth === 0) { setSelectedMonth(11); setSelectedYear(selectedYear - 1); }
    else setSelectedMonth(selectedMonth - 1);
  };
  const nextMonth = () => {
    if (selectedMonth === 11) { setSelectedMonth(0); setSelectedYear(selectedYear + 1); }
    else setSelectedMonth(selectedMonth + 1);
  };

  const getClosingDates = () => {
    return events.filter((e) => e.type === "closing").slice(0, 5);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-950 to-slate-900">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(99,102,241,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(99,102,241,0.03)_1px,transparent_1px)] bg-[size:4rem_4rem]" />
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-violet-500/10 rounded-full blur-3xl" />
        <div className="absolute top-40 right-1/4 w-80 h-80 bg-emerald-500/10 rounded-full blur-3xl" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
          {/* Top Bar */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-emerald-500 rounded-xl flex items-center justify-center shadow-lg shadow-violet-500/20">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <span className="font-['Barlow_Condensed'] text-3xl font-bold text-white tracking-tight">
                AllBaise
              </span>
              <span className="hidden sm:inline text-slate-300 text-base ml-2 font-bold">
                = Office Bespoke AI Scalable Engine
              </span>
            </div>
            <div className="flex items-center gap-3">
              <Button
                onClick={() => navigate("/landing")}
                variant="outline"
                className="border-slate-700 text-slate-300 hover:bg-slate-800"
              >
                Login
              </Button>
              <Button
                onClick={() => navigate("/landing?register=true")}
                className="bg-violet-500 hover:bg-violet-600 text-white"
              >
                Register
              </Button>
            </div>
          </div>

          {/* Hero Message */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-violet-500/10 border border-violet-500/20 rounded-full px-4 py-1.5 mb-6">
              <Sparkles className="w-4 h-4 text-violet-400" />
              <span className="text-sm text-violet-300">Your AI-powered workplace, unified.</span>
            </div>
            <h1 className="font-['Barlow_Condensed'] text-5xl sm:text-7xl font-bold text-white mb-4 tracking-tight">
              One Platform.
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-emerald-400">
                Infinite Possibilities.
              </span>
            </h1>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              An AI‑infused, expandable platform that automates daily office tasks in real time , all your tools, all in one place
            </p>
          </div>

          {/* AllBaise Agent Spotlight */}
          <div className="max-w-3xl mx-auto mb-12">
            <Link
              to="/agent"
              className="block relative group"
            >
              <div className="absolute -inset-1 bg-gradient-to-r from-violet-500 via-emerald-500 to-violet-500 rounded-2xl blur-xl opacity-30 group-hover:opacity-60 transition-opacity" />
              <div className="relative bg-slate-900 border border-slate-800 rounded-2xl p-8 flex flex-col sm:flex-row items-center gap-6">
                <div className="w-20 h-20 bg-gradient-to-br from-violet-500 to-emerald-500 rounded-2xl flex items-center justify-center shadow-lg shadow-violet-500/20 group-hover:scale-110 transition-transform">
                  <Bot className="w-10 h-10 text-white" />
                </div>
                <div className="flex-1 text-center sm:text-left">
                  <h2 className="font-['Barlow_Condensed'] text-2xl font-bold text-white mb-1">
                    Meet AllBaise Agent
                  </h2>
                  <p className="text-slate-400">
                    Ask anything about your documents, HR policies, courses, or organizational knowledge. Powered by cutting-edge AI.
                  </p>
                </div>
                <div className="flex items-center gap-2 text-violet-400 group-hover:text-violet-300 transition-colors">
                  <span className="text-sm font-medium">Ask Now</span>
                  <ArrowRight className="w-5 h-5" />
                </div>
              </div>
            </Link>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left: Broadcasts Feed */}
          <div className="lg:col-span-2 space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="font-['Barlow_Condensed'] text-2xl font-bold text-white flex items-center gap-2">
                <Megaphone className="w-6 h-6 text-violet-400" />
                Notice Board
              </h2>
              {auth.user && (
                <Link to="/comms-centre/broadcast">
                  <Button variant="outline" size="sm" className="border-slate-700 text-slate-300">
                    Manage Broadcasts
                  </Button>
                </Link>
              )}
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-violet-400" />
              </div>
            ) : broadcasts.length === 0 ? (
              <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-8 text-center">
                <Megaphone className="w-10 h-10 mx-auto text-slate-600 mb-3" />
                <p className="text-slate-400">No active broadcasts at the moment.</p>
                <p className="text-slate-500 text-sm mt-1">Check back soon for updates.</p>
              </div>
            ) : (
              broadcasts.slice(0, 10).map((b) => (
                <div
                  key={b.id}
                  className="bg-slate-900/50 border border-slate-800 rounded-xl p-6 hover:border-slate-700 transition-all"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${CATEGORY_COLORS[b.category] || "bg-slate-100 text-slate-700"}`}>
                      {b.category}
                    </span>
                    <span className="text-xs text-slate-500">
                      {b.created_at ? new Date(b.created_at).toLocaleDateString() : ""}
                    </span>
                    {b.expires_at && (
                      <span className="text-xs text-slate-500 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {Math.ceil((new Date(b.expires_at) - new Date()) / (1000 * 60 * 60 * 24))}d left
                      </span>
                    )}
                  </div>
                  <h3 className="font-['Barlow_Condensed'] text-xl font-bold text-white mb-2">{b.title}</h3>
                  <p className="text-slate-400 text-sm whitespace-pre-wrap line-clamp-3">{b.body}</p>
                  {b.closing_date && (
                    <div className="flex items-center gap-1 mt-3 text-xs text-amber-400">
                      <Calendar className="w-3 h-3" />
                      Closes: {new Date(b.closing_date).toLocaleDateString()}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Right: Calendar + Events */}
          <div className="space-y-6">
            {/* Calendar Widget */}
            <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5">
              <div className="flex items-center justify-between mb-4">
                <button onClick={prevMonth} className="text-slate-400 hover:text-white p-1">&larr;</button>
                <h3 className="font-['Barlow_Condensed'] text-lg font-bold text-white">
                  {new Date(selectedYear, selectedMonth).toLocaleString("default", { month: "long", year: "numeric" })}
                </h3>
                <button onClick={nextMonth} className="text-slate-400 hover:text-white p-1">&rarr;</button>
              </div>
              <div className="grid grid-cols-7 gap-1 text-center mb-2">
                {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => (
                  <div key={d} className="text-xs text-slate-500 py-1">{d}</div>
                ))}
              </div>
              <div className="grid grid-cols-7 gap-1 text-center">
                {Array.from({ length: firstDayOfWeek }).map((_, i) => (
                  <div key={`empty-${i}`} />
                ))}
                {Array.from({ length: daysInMonth }).map((_, i) => {
                  const day = i + 1;
                  const hasEvent = monthEvents.some(
                    (e) => new Date(e.date).getDate() === day
                  );
                  const isToday =
                    day === today.getDate() &&
                    selectedMonth === today.getMonth() &&
                    selectedYear === today.getFullYear();
                  return (
                    <div
                      key={day}
                      className={`p-1.5 text-sm rounded-lg ${
                        isToday
                          ? "bg-violet-500 text-white font-bold"
                          : hasEvent
                          ? "bg-emerald-500/20 text-emerald-300 font-medium"
                          : "text-slate-400 hover:bg-slate-800"
                      }`}
                    >
                      {day}
                      {hasEvent && !isToday && (
                        <div className="w-1 h-1 bg-emerald-400 rounded-full mx-auto mt-0.5" />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Upcoming Closings */}
            <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5">
              <h3 className="font-['Barlow_Condensed'] text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Calendar className="w-4 h-4 text-amber-400" />
                Closing Soon
              </h3>
              {getClosingDates().length === 0 ? (
                <p className="text-sm text-slate-500">No upcoming closing dates.</p>
              ) : (
                <div className="space-y-3">
                  {getClosingDates().map((ev) => (
                    <div key={ev.id} className="flex items-center gap-3 text-sm">
                      <div className="w-2 h-2 rounded-full bg-amber-400" />
                      <div className="flex-1">
                        <p className="text-slate-200">{ev.title.replace("Closing: ", "")}</p>
                        <p className="text-xs text-amber-400">
                          {new Date(ev.date).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4 text-center">
                <Megaphone className="w-5 h-5 text-violet-400 mx-auto mb-1" />
                <p className="text-2xl font-bold text-white">{broadcasts.length}</p>
                <p className="text-xs text-slate-500">Broadcasts</p>
              </div>
              <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4 text-center">
                <Calendar className="w-5 h-5 text-emerald-400 mx-auto mb-1" />
                <p className="text-2xl font-bold text-white">{events.length}</p>
                <p className="text-xs text-slate-500">Events</p>
              </div>
            </div>
          </div>
        </div>

        {/* Hubs Grid */}
        <div className="mt-12">
          <div className="flex items-center gap-2 mb-6">
            <Target className="w-5 h-5 text-violet-400" />
            <h2 className="font-['Barlow_Condensed'] text-2xl font-bold text-white">
              Explore All Hubs
            </h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {HUBS.map((hub) => {
              const Icon = hub.icon;
              return (
                <Link
                  key={hub.path}
                  to={hub.path}
                  className="group bg-slate-900/70 border border-slate-700/50 rounded-xl p-5 hover:border-violet-500/50 hover:bg-slate-900 transition-all text-center"
                >
                  <div
                    className={`w-12 h-12 bg-gradient-to-br ${hub.color} rounded-xl flex items-center justify-center mx-auto mb-3 shadow-lg group-hover:scale-110 transition-transform`}
                  >
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="font-['Barlow_Condensed'] text-base font-bold text-white group-hover:text-violet-300 transition-colors tracking-wide">
                    {hub.name}
                  </h3>
                  <p className="text-xs text-slate-400 mt-1 font-medium">{hub.desc}</p>
                </Link>
              );
            })}
          </div>
        </div>

        {/* Features Strip */}
        <div className="mt-12 bg-slate-900/30 border border-slate-800 rounded-2xl p-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: Cpu, label: "AI-Powered", desc: "Intelligent automation" },
              { icon: Shield, label: "Secure", desc: "Enterprise-grade security" },
              { icon: Globe, label: "Unified", desc: "All tools in one place" },
              { icon: Star, label: "Modern", desc: "Cutting-edge interface" },
            ].map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.label} className="text-center">
                  <Icon className="w-6 h-6 text-violet-400 mx-auto mb-2" />
                  <p className="font-semibold text-white text-sm">{f.label}</p>
                  <p className="text-xs text-slate-500">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-slate-800 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-slate-500 text-sm">
            AllBaise — Office Bespoke AI Scalable Engine. Powered by cutting-edge artificial intelligence.
          </p>
        </div>
      </footer>
    </div>
  );
}
